import { z } from 'zod'

/**
 * Base enum of every notification channel the backend emits. `global` is
 * managed by the backend and must not be exposed for user editing — consumers
 * that render channel controls should use `editableChannelsSchema` instead.
 */
export const notificationChannelsSchema = z.enum([
	'alerts',
	'air_user_management',
	'automation',
	'platform',
	'reports',
	'global',
])

/**
 * Enum for the notification types
 * @see https://docs.google.com/spreadsheets/d/17VQpKXkLxmElRG_5dKCVtpUXT_EQuSa6C-TPm9B-1eo/edit?gid=0#gid=0
 */
export const NotificationTypeEnum = z.enum([
	'user_created',
	'admin_user_created',
	'user_deleted',
	'user_role_changed',
	'user_promoted_to_admin',
	'playbook_started_manually',
	'playbook_started_automatically',
	'playbook_completed',
	'playbook_failed',
	'playbook_waiting_for_input',
	'new_integration_created',
	'integration_settings_updated',
	'integration_deleted',
	'report_is_ready',
])

/**
 * Channels that are user-editable from the settings UI (`global` excluded).
 */
export const editableChannelsSchema = notificationChannelsSchema.exclude([
	'global',
])

/**
 * Validation for creating a custom outbound notification recipient. Mirrors
 * backend constraints: non-empty `friendly_name` (max 255), valid email, and
 * at least one editable channel (`global` is excluded at the constants level).
 */
export const createOutboundNotificationSchema = z.object({
	friendly_name: z
		.string()
		.min(1, 'Friendly name is required')
		.max(255, 'Friendly name must be at most 255 characters'),
	email: z.email('Must be a valid email'),
	channels: z
		.array(editableChannelsSchema)
		.min(1, 'Select at least one channel'),
})

/**
 * Form-level validation for the edit-recipient form. Unlike the API schema,
 * all three fields are required at the UI layer because the form renders
 * pre-filled from the detail query — empty values are user edits, not
 * omissions.
 */
export const editReceiverFormSchema = z.object({
	friendly_name: z
		.string()
		.min(1, 'Friendly name is required')
		.max(255, 'Friendly name must be at most 255 characters'),
	email: z.email('Must be a valid email'),
	channels: z.array(editableChannelsSchema),
})
